import React from "react";

import { Box } from "@material-ui/core";

import useColourPalette from "components/visuals/useColourPalette";
import OutputAreasMap, { pointerInfoEmpty } from "components/visuals/maps/OutputAreasMap";
import SelectedStoreMarker from "components/visuals/maps/SelectedStoreMarker";

import Legend from "./Legend";

import { selectSelectedStore } from "modules/customer/tools/product/productSlice";
import { selectCatchmentCustomerProfiles, selectCatchmentAlignmentIsLoading, selectCatchmentAlignmentHasErrors } from "modules/customer/tools/product/productStoreFit/productStoreFitSlice";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { useAppSelector } from "store";

const Map: React.FC = () => {
    const colourPalette = useColourPalette();
    const colours = ["rgba(0,0,0,0)", ...colourPalette.categorical];
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const selectedStore = useAppSelector(selectSelectedStore);
    const customerProfiles = useAppSelector(selectCatchmentCustomerProfiles);
    const isLoading = useAppSelector(selectCatchmentAlignmentIsLoading);
    const hasErrors = useAppSelector(selectCatchmentAlignmentHasErrors);
    const [hoverInfo, setHoverInfo] = React.useState(pointerInfoEmpty);
    const hoveredCustomerProfile = customerProfiles.find(customerProfile => customerProfile.outputAreaCode === hoverInfo.outputAreaCode);

    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedStore?.latitude ?? 0,
        longitude: selectedStore?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    const mapOutputAreas = React.useMemo(() => {
        return customerProfiles.map(customerProfile => ({
            code: customerProfile.outputAreaCode,
            colourGroup: customerProfile.supergroupCode,
            opacity: customerProfile.probability
        }));
    }, [customerProfiles]);

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedStore?.latitude ?? 0,
                longitude: selectedStore?.longitude ?? 0
            };
        });
    }, [selectedStore]);

    return (
        <Box data-cy="catchment-area-customer-profiles">
            <OutputAreasMap
                loading={isLoading}
                error={hasErrors}
                title={"Catchment area customer profile classifications with percentage likelihood of visiting"}
                height={480}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                mapboxOutputAreasTilesetId={mapboxConfiguration.outputAreasTilesetId}
                mapboxOutputAreasTilesetUrl={mapboxConfiguration.outputAreasTilesetUrl}
                initialViewport={initialViewport}
                addGeocoder={false}
                outputAreas={mapOutputAreas}
                colours={colours}
                enableHover={true}
                onHover={setHoverInfo}
                enableClick={false}
                downloadData={customerProfiles}
            >
                <SelectedStoreMarker latitude={selectedStore?.latitude ?? 0} longitude={selectedStore?.longitude ?? 0} />
                <Legend catchmentCustomerProfile={hoveredCustomerProfile} />
            </OutputAreasMap>
        </Box>
    );
};

export default Map;
